import React, { forwardRef } from 'react';
import {
  Box,
  Typography,
  useTheme,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface CustomDatePickerProps {
  label?: string;
  value?: Dayjs | null;
  onChange?: (date: Dayjs | null) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  contrast?: boolean;
  sx?: object;
}

const DatePicker = forwardRef<HTMLDivElement, CustomDatePickerProps>(({
  label,
  value,
  onChange,
  error = false,
  helperText,
  disabled = false,
  fullWidth = true,
  contrast = true,
  sx = {},
  ...props
}, ref) => {
  const theme = useTheme();
  const borderColor = error ? '#E53E3E' : theme.palette.dark?.border;
  const textColor = disabled
    ? theme.palette.grey[500]
    : theme.palette.dark?.main;

  const combinedSx = {
    borderRadius: 4,
    width: fullWidth ? '100%' : 'auto',
    '& .MuiInputBase-root': {
      backgroundColor: contrast ? '#F2F4F7' : 'transparent',
      borderRadius: 4,
      border: `1px solid ${borderColor}`,
      '&:hover': {
        backgroundColor: contrast ? '#F2F4F7' : 'transparent',
        borderColor: borderColor,
      },
      '&.Mui-focused': {
        backgroundColor: contrast ? '#F2F4F7' : 'transparent',
        borderColor: borderColor,
      },
      '&.Mui-disabled': {
        backgroundColor: contrast ? '#F2F4F7' : 'transparent',
      },
    },
    '& .MuiInputBase-input': {
      color: textColor,
      WebkitTextFillColor: textColor,
      textAlign: 'right',
      direction: 'rtl',
      fontFamily: 'AlmoniDL',
      fontWeight: 700,
      padding: '20px 14px 12px 14px',
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
      fontFamily: 'AlmoniDL',
      fontWeight: 700,
      textAlign: 'right',
      right: 18,
      left: 'auto',
      transformOrigin: 'top right',
      direction: 'rtl',
      transform: 'translate(0, 16px) scale(1)',
      '&.Mui-focused, &.MuiInputLabel-shrink': {
        transform: 'translate(0, 3px) scale(0.75)',
        color: error ? '#E53E3E' : 'black',
      },
      '&.Mui-error': {
        color: '#E53E3E',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputAdornment-root': {
      position: 'absolute',
      left: '14px',
    },
    '& .MuiIconButton-root': {
      color: theme.palette.text.secondary,
      position: 'absolute',
      left: '14px',
    },
    ...sx,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box ref={ref} sx={{ position: 'relative', width: '100%', direction: 'rtl' }}>
        <MuiDatePicker
          label={label}
          value={value}
          onChange={onChange}
          disabled={disabled}
          minDate={dayjs()}
          slots={{
            openPickerIcon: CalendarTodayIcon,
            leftArrowIcon: ChevronRightIcon,
            rightArrowIcon: ChevronLeftIcon,
          }}
          slotProps={{
            textField: {
              variant: 'outlined',
              fullWidth: fullWidth,
              error: error,
            },
            openPickerButton: {
              color: 'inherit',
            },
            popper: {
              sx: {
                '& .MuiPaper-root': {
                  direction: 'rtl',
                  fontFamily: 'AlmoniDL',
                },
                '& .MuiPickersDay-root': {
                  fontFamily: 'AlmoniDL',
                  fontWeight: 600,
                  '&.Mui-selected': {
                    backgroundColor: '#C1FE40 !important',
                    color: 'black !important',
                    fontWeight: 700,
                    '&:hover': {
                      backgroundColor: '#B6F829 !important',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(193, 254, 64, 0.2)',
                  },
                },
                '& .MuiPickersDay-today': {
                  border: '2px solid #C1FE40 !important',
                  '&:not(.Mui-selected)': {
                    borderColor: '#C1FE40 !important',
                  },
                },
                '& .MuiPickersCalendarHeader-root': {
                  direction: 'rtl',
                },
                '& .MuiPickersCalendarHeader-label': {
                  fontFamily: 'AlmoniDL',
                  fontWeight: 700,
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  fontFamily: 'AlmoniDL',
                  fontWeight: 600,
                },
                '& .MuiIconButton-root': {
                  '&:hover': {
                    backgroundColor: 'rgba(193, 254, 64, 0.2)',
                  },
                },
                '& .MuiPickersArrowSwitcher-button': {
                  '&:hover': {
                    backgroundColor: 'rgba(193, 254, 64, 0.2)',
                  },
                },
              },
            },
          }}
          sx={combinedSx}
          {...props}
        />
        {error && helperText && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: '8px' }}>
            <ErrorOutlineIcon
              sx={{
                color: '#E53E3E',
                mr: '5px',
                height: '14px',
                width: '14px',
              }}
            />
            <Typography
              variant="body1"
              fontWeight="fontWeightRegular"
              fontSize={14}
              sx={{ color: '#E53E3E', fontFamily: 'AlmoniDL' }}
            >
              {helperText}
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
});

DatePicker.displayName = 'DatePicker';

export default DatePicker;
